import map from 'lodash/map';
import some from 'lodash/some';
import without from 'lodash/without';
import isNumber from 'lodash/isNumber';

const ACTION_COLUMN = {
    ariaLabel: 'Actions',
    fieldName: 'actions',
    fieldLabel: '',
    isEditable: false,
    isSortable: false,
};

const _stringSortHelper = (isDescending, valueA, valueB) => {
    if (isDescending) {
        return valueB.toString().localeCompare(valueA);
    }

    return valueA.toString().localeCompare(valueB);
};

const _intSortHelper = (isDescending, valueA, valueB) =>
    isDescending ? valueB - valueA : valueA - valueB;

const _sortHelper = (column, isDescending, itemA, itemB) => {
    const valueA = itemA[column];
    const valueB = itemB[column];

    if (isNumber(valueA) && isNumber(valueB)) {
        return _intSortHelper(isDescending, valueA, valueB);
    }

    return _stringSortHelper(isDescending, valueA, valueB);
};

const _sortItems = ({ filteredItems, currentSortColumn, sortDescending }) => {
    let sortedItems = [...filteredItems];
    const sortHelperWithArgs = _sortHelper.bind(
        null,
        currentSortColumn,
        sortDescending,
    );

    if (currentSortColumn) {
        sortedItems = sortedItems.sort(sortHelperWithArgs);
    }

    return sortedItems;
};

export const getFilteredItems = ({
    items,
    columns: filterColumns,
    filterText,
    filterPredicate,
}) => {
    let filteredItems = items;

    if (filterText) {
        filteredItems = items.filter((item) =>
            filterPredicate(filterText, item, filterColumns),
        );
    }

    return filteredItems;
};

export const getFilteredAndSortedItems = ({
    items,
    columns,
    filterText,
    filterPredicate,
    sortDescending,
    currentSortColumn,
}) => {
    const filteredItems = getFilteredItems({
        items,
        columns,
        filterText,
        filterPredicate,
    });
    const filteredAndSortedItems = _sortItems({
        filteredItems,
        sortDescending,
        currentSortColumn,
    });

    return filteredAndSortedItems;
};

export const getPaginatedItems = (items, currentPage, currentPageSize) => {
    const itemsLength = items.length;

    if (itemsLength <= currentPageSize) {
        return items;
    }

    const startPoint = (currentPage - 1) * currentPageSize;
    const endPoint = currentPage * currentPageSize;

    return items.slice(startPoint, endPoint);
};

export const getColumns = (columns, itemActions) => {
    const hasEditableColumns = some(columns, (column) => column.isEditable);
    let newColumns = columns;

    if (hasEditableColumns || itemActions) {
        newColumns = [...columns, ACTION_COLUMN];
    }

    return newColumns;
};

export const getPageSize = (pageSize, pageSizes, items) => {
    let defaultPageSize = items.length;

    if (pageSizes) {
        defaultPageSize = pageSizes[0];
    }

    return pageSize || defaultPageSize;
};

export const getSelectedItemIds = (checked, items, itemIds = []) =>
    checked ? map(items, 'id') : itemIds;

export const getUpdatedSelectedItems = (id, itemIds = [], checked = false) =>
    checked ? [...itemIds, id] : without(itemIds, id);

/** The default comparator function for sorting and filtering a data table */
export const defaultFilterPredicate = (filterText, item, filterableColumns) => {
    const filter = filterText.toString().toLocaleLowerCase();

    return some(filterableColumns, (column) => {
        const val = item[column.fieldName].toString().toLocaleLowerCase();

        return val.indexOf(filter) > -1;
    });
};
